import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowRight, ArrowUpRight, Beaker, Package, Globe } from "lucide-react";
import { fetcher } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = [
  {
    q: "What is the minimum order quantity (MOQ)?",
    a: "MOQ starts from 500 units for most SKUs. Custom formulations may require 1,000+ units depending on the complexity of the formula and packaging.",
  },
  {
    q: "Can I order a small sample first?",
    a: "Yes. Every product supports a paid sample order with your branding, packaging and label finish — starting at Rs. 999.",
  },
  {
    q: "Do you support private label & OEM?",
    a: "Both. Choose from 600+ ready formulas for private label or co-develop a bespoke formula through our R&D lab.",
  },
  {
    q: "Which countries do you ship to?",
    a: "We export to 30+ countries with full regulatory support — including UAE, USA, UK, Australia, Singapore, Kenya and more.",
  },
  {
    q: "What certifications does Fresio hold?",
    a: "GMP, ISO certified, CDSCO-registered facility, FDA export documentation and cruelty-free processes.",
  },
];

export default function CategoryDetail() {
  const { slug } = useParams();
  const { data: cat } = useSWR(slug ? `/categories/${slug}` : null, fetcher);

  if (!cat) {
    return (
      <div className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="font-editorial text-4xl text-[#1D3A2F]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="pt-28">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 py-16 lg:py-24">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5">Category · {cat.slug}</div>
            <h1 className="font-editorial text-6xl md:text-8xl text-[#1D3A2F] leading-[0.95]">
              {cat.name}
            </h1>
            <p className="mt-6 text-lg text-[#556B57] italic">
              {cat.tagline}
            </p>
            <p className="mt-8 max-w-xl text-lg text-[#222222]/75 leading-relaxed">
              {cat.description}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/order-sample"
                data-testid="category-order-sample"
                className="inline-flex items-center gap-3 h-13 py-4 px-7 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[13px] tracking-[0.16em] uppercase font-medium hover:bg-black transition"
              >
                Order Sample <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 py-4 px-7 rounded-full border border-[#1D3A2F]/30 text-[#1D3A2F] text-[13px] tracking-[0.16em] uppercase hover:border-[#1D3A2F] transition"
              >
                Contact Expert <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-8 max-w-md">
              <div>
                <div className="eyebrow mb-2">Market Size</div>
                <div className="font-editorial text-3xl text-[#1D3A2F]">
                  {cat.market_size}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Growth</div>
                <div className="font-editorial text-3xl text-[#1D3A2F]">
                  {cat.cagr}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-[#FDFBF7]">
              <img
                src={cat.hero_image}
                alt={cat.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4">Featured products</div>
            <h2 className="font-editorial text-5xl text-[#1D3A2F]">
              Shop the {cat.name.toLowerCase()} range.
            </h2>
          </div>
          <p className="text-sm text-[#556B57]">
            {cat.products?.length || 0} formulas ready to private-label
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {cat.products.map((p) => (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              data-testid={`cat-product-${p.slug}`}
              className="group"
            >
              <div className="relative rounded-3xl overflow-hidden bg-[#FDFBF7] aspect-[3/4] mb-4">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                {p.best_seller && (
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[10px] tracking-[0.18em] uppercase">
                    Bestseller
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-editorial text-xl text-[#1D3A2F]">
                    {p.name}
                  </div>
                  <div className="text-xs text-[#556B57] mt-1">{p.tagline}</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#1D3A2F] mt-1 opacity-40 group-hover:opacity-100 transition" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Manufacturing Highlights */}
      <section className="bg-[#FDFBF7] border-y border-[#D7D4CF]/50 py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          {[
            { icon: Beaker, t: "R&D Lab", d: "In-house chemists develop 40+ new formulas every quarter." },
            { icon: Package, t: "Packaging Studio", d: "Bottles, tubes, jars, droppers — matte or gloss, foil or emboss." },
            { icon: Globe, t: "Export Ready", d: "Documentation for CDSCO, FDA, CPNP, ASEAN and more." },
          ].map(({ icon: Icon, t, d }) => (
            <div
              key={t}
              className="rounded-3xl bg-[#F7F4EE] p-8 border border-[#D7D4CF]/40"
            >
              <div className="w-12 h-12 rounded-full bg-[#FDFBF7] flex items-center justify-center border border-[#D7D4CF]">
                <Icon className="w-5 h-5 text-[#1D3A2F]" strokeWidth={1.4} />
              </div>
              <div className="font-editorial text-2xl text-[#1D3A2F] mt-6">{t}</div>
              <p className="text-sm text-[#222222]/70 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trending Actives */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 border-t border-[#D7D4CF]/50">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Trending actives · 2026</div>
            <h2 className="font-editorial text-4xl md:text-5xl text-[#1D3A2F] leading-[1.05]">
              The ingredients your
              <br />
              <em className="gold-text">customers are searching for.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-[#222222]/70 leading-relaxed">
              Fresio's R&amp;D team tracks 400+ ingredient trends monthly across
              TikTok, Sephora, Amazon and Nykaa. These are the actives that
              convert in {cat.name.toLowerCase()} right now — and the ones
              you'll find inside our formula library.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {[
            "PDRN",
            "Niacinamide 10%",
            "Vitamin C Ester",
            "Ceramides",
            "Peptides",
            "Hyaluronic Acid",
            "Retinal",
            "Bakuchiol",
            "Azelaic Acid",
            "Kojic Acid",
            "Snail Mucin",
            "Bio-Fermented",
            "Exosomes",
            "Rice Water",
            "Centella Asiatica",
            "Salicylic 2%",
            "Squalane",
            "Tranexamic Acid",
          ].map((a) => (
            <span
              key={a}
              className="px-5 py-2.5 rounded-full bg-[#FDFBF7] border border-[#D7D4CF] text-sm text-[#1D3A2F]"
            >
              {a}
            </span>
          ))}
        </div>
      </section>

      {/* Who Buys This */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Who ships {cat.name.toLowerCase()} with Fresio</div>
            <h3 className="font-editorial text-4xl text-[#1D3A2F] leading-[1.05]">
              Six founder archetypes.
              <br />
              <em className="gold-text">All welcome.</em>
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { t: "D2C Founders", d: "Building a next-gen Shopify beauty brand" },
            { t: "Salons & Clinics", d: "Launching an in-house branded line" },
            { t: "Influencers", d: "Converting community into product" },
            { t: "Retail Distributors", d: "Curating shelf-ready portfolios" },
            { t: "Export Buyers", d: "Sourcing India-made premium formulas" },
            { t: "Corporate Gifting", d: "Custom curated hamper editions" },
          ].map((p) => (
            <div
              key={p.t}
              className="rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-6"
            >
              <div className="font-editorial text-2xl text-[#1D3A2F]">
                {p.t}
              </div>
              <div className="text-sm text-[#556B57] mt-2 leading-relaxed">
                {p.d}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Launch Timeline */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-10 md:p-14">
          <div className="eyebrow text-[#FDFBF7]/60 mb-4">
            Your 72-hour launch
          </div>
          <h3 className="font-editorial text-4xl leading-[1.05] mb-10">
            From order to your first sample.
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { h: "0 h", t: "Order placed", d: "Payment confirmed via Razorpay live checkout." },
              { h: "12 h", t: "Formulation", d: "R&D lab locks your plan, actives and stability check." },
              { h: "36 h", t: "Filling + Labels", d: "Container filled · label finish applied · logo printed." },
              { h: "72 h", t: "Delivered", d: "Sample dispatched with tracking to your address." },
            ].map((s) => (
              <div
                key={s.h}
                className="rounded-2xl border border-[#FDFBF7]/15 p-5"
              >
                <div className="font-editorial text-3xl">{s.h}</div>
                <div className="text-sm font-medium mt-4">{s.t}</div>
                <div className="text-xs text-[#FDFBF7]/60 mt-2 leading-relaxed">
                  {s.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-24">
        <div className="eyebrow mb-5">Frequently Asked</div>
        <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05] mb-10">
          Everything you want to know
          <br />
          before you launch.
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQ.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border border-[#D7D4CF]/60 rounded-2xl px-6 bg-[#FDFBF7]"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-editorial text-xl text-[#1D3A2F] py-5 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#222222]/75 text-base leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
